/**
 * Which of the three tabs an expense belongs to on the Expenses page, and what
 * each tab adds up to.
 *
 * This lives here rather than inline in the page because it decides whether an
 * expense counts toward a total the owner reads as fact. Getting it wrong is
 * not a display bug — it is a wrong number on a tax product.
 *
 * THE RULE. An expense is in exactly one of three places:
 *
 *   529-eligible      the owner said yes
 *   Non-529           the owner said no
 *   Needs a decision  the owner has not answered yet  ("draft")
 *
 * The third is NOT a third kind of eligibility. Eligibility is yes or no; the
 * third place is a waiting room that empties as the owner works through it.
 * Its contents count toward NOTHING — not either tab's total, not the Overview,
 * not a tax document.
 *
 * WHAT WAS WRONG BEFORE (ZEN-113). The page split on eligibility alone:
 *
 *   tab529 = expenses.filter(e => e.eligible_529)
 *   tabNon = expenses.filter(e => !e.eligible_529)
 *
 * An unanswered expense is not marked eligible, so it fell into the Non-529 tab
 * and its amount was added into that tab's total — the app answering "no" on the
 * owner's behalf, while an amber banner three inches below insisted a decision
 * was still owed.
 *
 * WHY THE WAITING ROOM IS PASSED IN SEPARATELY. The two settled tabs are filtered
 * by tax year; the waiting room is not, and comes from its own request. A room
 * whose purpose is to empty must not be able to hide items behind a filter — an
 * unanswered expense from another tax year would otherwise be invisible here
 * while the Overview kept counting it, which is a dead end: the owner is told
 * work is waiting and shown an empty screen.
 */

export const TAB_529 = "529";
export const TAB_NON529 = "non529";
export const TAB_NEEDS_DECISION = "needs_decision";

/** An expense nobody has finished classifying. */
export const isUnclassified = (e) => e?.status === "draft";

/** Is this row's dollar figure usable in a figure shown on screen?
 *
 * ★ THE BROWSER TELLS YOU; THE SERVER DECIDES. This is a DISPLAY rule and
 * nothing more — it decides what a screen adds up and what it marks, never what
 * the application permits. Every rule about what money may be RECORDED lives on
 * the server, where it cannot be walked past.
 *
 * That boundary is stated because a review round correctly pointed out that
 * this test accepts any finite number, while the server also refuses a negative
 * figure and one above the amount the app can record. Duplicating those bounds
 * here would look like rigour and would not be: a value that breaks them can
 * only reach this code from a browser cache predating this change, or from
 * somebody editing their own browser state — and against the second, no
 * browser-side check means anything at all. (The first is measured, not
 * assumed: ZEN-467 counted the stored rows on 2026-09-01 and found zero
 * outside those bounds.)
 *
 * So this asks the two questions a screen can honestly answer — did the server
 * flag this row, and is the amount a real number — and leaves the bounds where
 * they are enforced. A guard that cannot be authoritative should not pretend to
 * be; that pretence is exactly what got the settlement guard taken out of this
 * change and sent to ZEN-637, where the server side is fixed too.
 * (Adversarial review, rounds 8 and 9; owner ruling to split, 2026-09-15.)
 */
export const isPricedRow = (e) => {
  // The SERVER's verdict first — it is the rule, and it knows the negative and
  // ceiling bounds the browser cannot see. Only `=== true` counts: a row from
  // an old cache carries no such field, and "I was not told" must never read as
  // "the server flagged this".
  if (e?.is_unpriced === true) return false;
  // Then a guarantee rather than a rule: whatever the server said, a value that
  // is not a real number never reaches a figure a family reads. This is also
  // what covers the old cached row — no verdict, no usable amount, not priced.
  const v = e?.usd_amount;
  return typeof v === "number" && Number.isFinite(v);
};

/** An expense the app could not convert into US dollars.
 *
 * THE SERVER DECIDES THIS, NOT THE BROWSER, and that is the whole point.
 * `is_unpriced` is stamped on every expense by the one serializer, from the
 * same function every money total uses (backend/money_totals.py). Testing
 * `!e.usd_amount` here instead would agree today and drift tomorrow — that rule
 * also refuses a negative figure and one larger than the app can record, and
 * nothing in the browser knows either bound. Re-deriving it here would rebuild
 * the exact defect ZEN-632 removed: two places answering one question.
 *
 * ONE DEFINITION, TWO NAMES. This is `!isPricedRow` and nothing else. They were
 * two slightly different tests for one round, and that was enough to break the
 * promise the whole change rests on: a row from a browser cache holding no
 * `is_unpriced` field but an empty amount was LEFT OUT of the totals and NOT
 * flagged — so the page could report an expense missing from a figure and then
 * show an empty list when the reader asked to see it. Exactly the count-and-
 * marks disagreement there is a server-side test forbidding.
 * (Adversarial review, round 8.) */
export const needsAManualRate = (e) => !isPricedRow(e);

/** Money the family has not actually spent yet — an expense recorded but not paid.
 *
 * THE FRONT END HAS ITS OWN COPY OF THIS RULE and it was missing, which is how a
 * $5,000 unpaid rent row still moved the totals printed on the Expenses page.
 * The server-side guard covers nine endpoints and proves scheduled money reaches
 * no figure; it cannot see a total the browser adds up for itself. Same rule as
 * database.NOT_SPENT_STATUSES, restated here because this is where the number the
 * owner reads is computed. (ZEN-109; found by the pre-merge review)
 */
export const isNotYetPaid = (e) => {
  if (e?.status === "scheduled") return true;
  // The status is a cache; the DATE is the truth. The backfill deliberately
  // leaves legacy future-dated rows that are tied to a distribution with their
  // old spent status (re-statusing would contradict a filed record), so a
  // status-only test counted them as money spent. Same two-part rule as the
  // server's spent filter — and a blank date cannot establish payment either.
  const d = (e?.transaction_date || "").slice(0, 10);
  if (!d) return true;
  return d > new Date().toISOString().slice(0, 10);
};

/** 529-eligible, PAID, and not already drawn from the plan.

 * The paid test is the shared two-part rule, not the status alone: a legacy row
 * with a blank date and a "confirmed" status is not money anyone can withdraw
 * against — no payment date, no payment. (Pre-merge review, round 2.) */
export const eligibleToWithdraw = (e) =>
  e.eligible_529 && e.status === "confirmed" && e.payor_ref !== "529" && !isNotYetPaid(e);

/** Add up dollar figures, LEAVING OUT what could not be priced, and say how many.
 *
 * ★ THE BROWSER ADDS MONEY UP TOO, AND IT WAS STILL ANSWERING THE OLD WAY.
 *
 * ZEN-632 put "what does a row with no dollar figure contribute to a total?"
 * in one place on the server and had every total ask it — and then stopped at
 * the server. Seven totals in the browser were still writing `|| 0`, which
 * counts an expense nobody could price as ZERO. So the Overview said "not
 * counted in any total" while this page quietly counted it as nothing: two
 * screens, the same money, two different answers. That is precisely the
 * defect ZEN-44 corrected for gifts and the one this whole ticket exists for,
 * reached by sweeping half the distance.
 *
 * Returns the count as well as the total, for the same reason the server's
 * `priced_total` does: leaving money out of a figure is something the reader
 * is entitled to know about, and a helper that only returns a number makes
 * silence the easy option.
 *
 * TWO CHECKS, AND THEY ARE NOT THE SAME CHECK. The first asks the SERVER's
 * verdict (`is_unpriced`), which is the rule — it knows about negative figures
 * and the app's ceiling, and the browser must never try to re-derive that. The
 * second refuses a value that is not a real number, which is not the rule but
 * a guarantee: whatever happens, this cannot put "not a number" on screen in
 * place of a total. (ZEN-632)
 */
export const pricedSum = (list = []) => {
  let total = 0;
  let unpriced = 0;
  for (const e of list) {
    if (!isPricedRow(e)) {
      unpriced += 1;
      continue;
    }
    total += e.usd_amount;
  }
  return { total, unpriced };
};

const sumUsd = (list) => pricedSum(list).total;

/**
 * Split the expenses across the three tabs and apply the toolbar filters.
 *
 * @param {object}   args
 * @param {Array}    args.expenses           the year-filtered list from GET /expenses
 * @param {Array}    args.waiting            unclassified expenses, ALL tax years
 * @param {string}   args.activeTab          TAB_529 | TAB_NON529 | TAB_NEEDS_DECISION
 * @param {boolean}  args.eligibleOnly       "529-eligible to withdraw" chip
 * @param {boolean}  args.hideReconciled     "Hide reconciled/closed" chip
 * @param {boolean}  args.missingReceiptOnly "Missing receipts only" chip
 * @param {boolean}  args.needsRateOnly       "Needs an exchange rate" chip
 */
export function splitExpenseTabs({
  expenses = [],
  waiting = [],
  activeTab = TAB_529,
  eligibleOnly = false,
  hideReconciled = false,
  missingReceiptOnly = false,
  needsRateOnly = false,
  onlyIds = null,
} = {}) {
  // Belt and braces: `waiting` arrives from its own request, but the settled
  // tabs are also filtered here so a draft can never reach a total even if the
  // two lists overlap.
  const settled = expenses.filter((e) => !isUnclassified(e));
  const tab529 = settled.filter((e) => e.eligible_529);
  const tabNon = settled.filter((e) => !e.eligible_529);
  // The rows STAY IN THE LISTS — the owner needs to see next month's rent — but
  // they are excluded from the TOTALS, because an expense nobody has paid is not
  // money spent. Two different questions about the same row, and collapsing them
  // is what put unpaid rent into a figure on screen.
  const tab529Spent = tab529.filter((e) => !isNotYetPaid(e));
  const tabNonSpent = tabNon.filter((e) => !isNotYetPaid(e));

  const onWaitingTab = activeTab === TAB_NEEDS_DECISION;
  const tabExpenses = onWaitingTab
    ? waiting
    : activeTab === TAB_529
      ? tab529
      : tabNon;

  // The toolbar filters are NOT applied in the waiting room. "Missing receipts
  // only" excludes drafts outright and "529-eligible to withdraw" requires a
  // settled status, so either one would empty a room the Overview says is full —
  // the owner would be told work is waiting and shown nothing. Both questions
  // are meaningless about a record with no decision. "Hide reconciled/closed" is
  // left applied because a draft is never either, so it is inert.
  let baseList = tabExpenses;
  // ★ THIS ONE FILTER SPANS THE TABS, AND IT HAS TO.
  //
  // The Overview counts every expense that could not be priced, whatever its
  // eligibility, and sends the owner here with this filter on to answer
  // "which one?". Applied INSIDE the active tab it would hide a non-529 or
  // still-undecided expense behind a tab nobody told them to click — landing
  // them on an empty screen immediately after being told an expense needs
  // them. That is the exact dead end this whole prompt exists to remove, so
  // rebuilding it one layer down would have been worse than not linking at all.
  //
  // The page opens on the 529 tab, so this was not a corner case: any unpriced
  // expense that is non-529 or undecided was simply invisible.
  //
  // Deduplicated by id because the waiting room arrives from its own request
  // and can overlap the settled list — counting a row twice would tell the
  // family there are more problems than there are.
  //
  // (Adversarial review, round 5. It was right, and it also spotted that MY OWN
  // test had masked it: the draft case selected the waiting-room tab by hand,
  // which the real link never does.)
  const everyTab = () => {
    const byId = new Map();
    for (const e of [...settled, ...waiting]) {
      if (e?.id != null && !byId.has(e.id)) byId.set(e.id, e);
    }
    return [...byId.values()];
  };
  if (needsRateOnly) baseList = everyTab().filter(needsAManualRate);
  // The expenses an Overview warning counted, across the tabs for the same
  // reason as the filter above. (ZEN-768)
  if (onlyIds) {
    const wanted = new Set(onlyIds);
    baseList = everyTab().filter((e) => wanted.has(e.id));
  }
  // The other chips do not narrow a warning's list: it promises exactly the
  // expenses the warning counted. (ZEN-768)
  const narrowing = !onlyIds;
  if (narrowing && !onWaitingTab && eligibleOnly) baseList = baseList.filter(eligibleToWithdraw);
  if (narrowing && hideReconciled) {
    baseList = baseList.filter((e) => e.status !== "reconciled" && e.status !== "closed");
  }
  if (narrowing && !onWaitingTab && missingReceiptOnly) {
    baseList = baseList.filter(
      (e) => e.status !== "draft" && !e.attachments?.length,
    );
  }
  // (The needs-a-rate filter is applied ABOVE, before the tab-scoped chips,
  // because it replaces the tab selection rather than narrowing it.)

  return {
    waiting,
    settled,
    tab529,
    tabNon,
    tab529Total: sumUsd(tab529Spent),
    tabNonTotal: sumUsd(tabNonSpent),
    // How many rows the tab totals above leave out because nobody could price
    // them. Over the rows ON SCREEN, so the page can say what the figures
    // beside them are missing rather than leaving the reader to wonder. The
    // Overview reports the same thing for its own scope. (ZEN-632)
    unpricedOnScreen: pricedSum(baseList).unpriced,
    tab529Scheduled: sumUsd(tab529.filter(isNotYetPaid)),
    tabExpenses,
    baseList,
    onWaitingTab,
  };
}
